
import { useState, useEffect, useRef } from 'react';
import { Card, Form, Input, Button, Avatar, Switch, Popup, Toast } from 'antd-mobile';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Image as ImageIcon,
    Settings,
    LogOut,
    CreditCard,
    ChevronRight,
    Moon,
    User,
    Store,
    Users,
    Edit2,
    Lock,
    ArrowLeft,
    Crown,
    Clock,
    Zap,
    Camera,
    Upload
} from 'lucide-react';
import { useStore } from '../store';
import { useLiff } from '../hooks/useLiff';
import { api } from '../api';
import './ProfilePage.css';

export default function ProfilePage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const user = useStore((s) => s.user);
    const dbUser = useStore((s) => s.dbUser);
    const setDbUser = useStore((s) => s.setDbUser);
    const promptPay = useStore((s) => s.promptPay);
    const setPromptPay = useStore((s) => s.setPromptPay);
    const showToast = useStore((s) => s.showToast);
    const themeMode = useStore((s) => s.themeMode);
    const setThemeMode = useStore((s) => s.setThemeMode);
    const { logout } = useLiff();

    // PromptPay state
    const [showPromptPay, setShowPromptPay] = useState(false);
    const [number, setNumber] = useState(promptPay.number);
    const [name, setName] = useState(promptPay.name);
    const [saving, setSaving] = useState(false);
    const [cameFromQR, setCameFromQR] = useState(false);

    // Shop Info Editing (Name + Image)
    const [showShopEdit, setShowShopEdit] = useState(false);
    const [shopName, setShopName] = useState('');
    const [shopImageFile, setShopImageFile] = useState<File | null>(null);
    const [shopImagePreview, setShopImagePreview] = useState<string | null>(null);
    const [savingShop, setSavingShop] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Auto-open PromptPay settings
    useEffect(() => {
        if (searchParams.get('edit') === 'promptpay') {
            setNumber(promptPay.number);
            setName(promptPay.name);
            setShowPromptPay(true);
            setCameFromQR(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, promptPay.number, promptPay.name, setSearchParams]);

    const openPromptPaySettings = () => {
        setNumber(promptPay.number);
        setName(promptPay.name);
        setShowPromptPay(true);
    };

    // Shop Edit Logic
    const openShopEdit = () => {
        setShopName(dbUser?.shop_name || '');
        setShopImagePreview(dbUser?.shop_image || null);
        setShopImageFile(null);
        setShowShopEdit(true);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setShopImageFile(file);
            // Create local preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setShopImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveShopInfo = async () => {
        if (!dbUser) return;
        setSavingShop(true);

        try {
            let imageUrl = dbUser.shop_image; // Keep existing by default

            // 1. Upload Image (if changed)
            if (shopImageFile) {
                const uploadRes = await api.uploadImage(shopImageFile);
                if (uploadRes.success) {
                    imageUrl = uploadRes.url;
                }
            }

            // 2. Update User Profile
            const result = await api.updateUser(dbUser.line_user_id, {
                shop_name: shopName.trim(),
                shop_image: imageUrl
            });

            if (result.data) {
                setDbUser(result.data);
                setShowShopEdit(false);
                showToast('บันทึกข้อมูลร้านสำเร็จ!');
            }
        } catch (error) {
            console.error('Save shop info error:', error);
            showToast('เกิดข้อผิดพลาดในการบันทึก');
        } finally {
            setSavingShop(false);
        }
    };

    const handleSavePromptPay = async () => {
        setSaving(true);
        try {
            await api.savePromptPaySettings(dbUser?.line_user_id || '', {
                number,
                name,
                type: 'phone',
            });
            setPromptPay({ ...promptPay, number, name });
            setShowPromptPay(false);
            showToast('บันทึก PromptPay สำเร็จ!');
            if (cameFromQR) {
                setCameFromQR(false);
                navigate('/qr');
            }
        } catch (error) {
            showToast('เกิดข้อผิดพลาด');
        } finally {
            setSaving(false);
        }
    };

    const MenuCard = ({ icon, title, subtitle, onClick, extra }: any) => (
        <div className="menu-card" onClick={onClick}>
            <div className="menu-icon-wrapper">
                {icon}
            </div>
            <div className="menu-content">
                <div className="menu-title">{title}</div>
                {subtitle && <div className="menu-subtitle">{subtitle}</div>}
            </div>
            <div className="menu-action">
                {extra || <ChevronRight size={20} color="#ddd" />}
            </div>
        </div>
    );

    // Dynamic Header Background
    const headerStyle = dbUser?.shop_image
        ? {
            backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.6)), url(${dbUser.shop_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
        }
        : { background: '#50b6ff' };

    return (
        <div className="page profile-page-refined">
            {/* Header Section with Dynamic Background */}
            <div className="profile-header-bg" style={headerStyle}>
                <div className="profile-user-info">
                    <div className="profile-text">
                        <h1>{user?.displayName || 'ผู้ใช้งาน'}</h1>
                        <div
                            onClick={openShopEdit}
                            className="shop-name-badge"
                        >
                            <Store size={14} />
                            <span>{dbUser?.shop_name || 'ตั้งชื่อร้านค้า'}</span>
                            <Edit2 size={12} style={{ opacity: 0.7 }} />
                        </div>
                    </div>
                    <Avatar
                        src={user?.pictureUrl || ''}
                        className="profile-avatar"
                        style={{ '--size': '80px' }}
                    />
                </div>
            </div>

            <div className="profile-content-scroll">
                <div className="section-title">เมนูจัดการ</div>

                {/* PromptPay Settings */}
                <MenuCard
                    icon={<CreditCard size={24} />}
                    title="ตั้งค่า PromptPay"
                    subtitle={promptPay.number ? `${promptPay.number}` : 'สำหรับรับเงิน QR'}
                    onClick={openPromptPaySettings}
                />

                {/* Slip Album */}
                <MenuCard
                    icon={<ImageIcon size={24} />}
                    title="อัลบั้มสลิป"
                    subtitle="ประวัติการโอนเงิน"
                    onClick={() => navigate('/slips')}
                />

                {/* Staff Management - Only for PRO/TEST_PRO owners */}
                {dbUser?.role === 'OWNER' && (dbUser?.subscription_tier === 'PRO' || dbUser?.subscription_tier === 'TEST_PRO') && (
                    <MenuCard
                        icon={<Users size={24} />}
                        title="จัดการทีม (Staff)"
                        subtitle="เพิ่มพนักงานช่วยขาย"
                        onClick={() => navigate('/staff')}
                    />
                )}

                {/* Dark Mode */}
                <MenuCard
                    icon={<Moon size={24} />}
                    title="โหมดมืด"
                    extra={
                        <Switch
                            checked={themeMode === 'dark'}
                            onChange={(checked) => setThemeMode(checked ? 'dark' : 'light')}
                            style={{ '--checked-color': '#50b6ff' }}
                        />
                    }
                />

                <div className="section-title">บัญชี</div>

                <MenuCard
                    icon={<LogOut size={24} />}
                    title="ออกจากระบบ"
                    onClick={logout}
                />

                <div className="app-version">Version 2.1.0</div>
            </div>

            {/* Shop Edit Modal */}
            <Popup
                visible={showShopEdit}
                onMaskClick={() => setShowShopEdit(false)}
                bodyStyle={{ borderTopLeftRadius: '24px', borderTopRightRadius: '24px', minHeight: '600px' }}
                className="custom-popup-body"
            >
                <div className="pp-container">
                    <div className="pp-content">
                        <div className="pp-header-row">
                            <div className="pp-title">ข้อมูลร้านค้า</div>
                            <button onClick={() => setShowShopEdit(false)} className="close-btn">X</button>
                        </div>

                        {/* Image Upload Section */}
                        <div className="shop-image-upload-area">
                            <div
                                className="shop-image-preview"
                                onClick={() => fileInputRef.current?.click()}
                                style={shopImagePreview ? { backgroundImage: `url(${shopImagePreview})` } : {}}
                            >
                                {!shopImagePreview && (
                                    <div className="upload-placeholder">
                                        <Camera size={40} color="#ccc" />
                                        <span>แตะเพื่อเพิ่มรูปร้าน</span>
                                    </div>
                                )}
                                {shopImagePreview && (
                                    <div className="edit-overlay">
                                        <Camera size={20} color="#fff" />
                                    </div>
                                )}
                            </div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                hidden
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                            <div className="upload-hint">แนะนำรูปแนวนอน (Cover) จะสวยที่สุด</div>
                        </div>

                        {/* Name Input */}
                        <div className="pp-form-group">
                            <label className="pp-label">ชื่อร้าน</label>
                            <input
                                type="text"
                                className="pp-input"
                                placeholder="เช่น ร้านกาแฟรักคุณ"
                                value={shopName}
                                onChange={(e) => setShopName(e.target.value)}
                                maxLength={50}
                            />
                        </div>

                        {/* Actions */}
                        <div className="pp-actions">
                            <button
                                className="pp-btn-save"
                                onClick={handleSaveShopInfo}
                                disabled={savingShop}
                            >
                                {savingShop ? 'กำลังอัปโหลด...' : 'บันทึกข้อมูล'}
                            </button>
                        </div>
                    </div>
                </div>
            </Popup>

            {/* PromptPay Popup (Existing) */}
            <Popup
                visible={showPromptPay}
                onMaskClick={() => setShowPromptPay(false)}
                bodyStyle={{ borderTopLeftRadius: '20px', borderTopRightRadius: '20px', minHeight: '500px' }}
                className="custom-popup-body"
            >
                <div className="pp-container">
                    <div className="pp-content">
                        <div className="pp-title">PromptPay รับเงิน</div>
                        <div className="pp-form-group">
                            <label className="pp-label">ชื่อบัญชี</label>
                            <input
                                type="text"
                                className="pp-input"
                                placeholder="..."
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                        <div className="pp-form-group">
                            <label className="pp-label">เบอร์ / เลขบัตร</label>
                            <input
                                type="text"
                                className="pp-input"
                                placeholder="..."
                                value={number}
                                onChange={(e) => setNumber(e.target.value)}
                            />
                        </div>
                        <div className="pp-actions">
                            <button className="pp-btn-save" onClick={handleSavePromptPay} disabled={saving}>
                                {saving ? 'บันทึก...' : 'บันทึก'}
                            </button>
                        </div>
                    </div>
                </div>
            </Popup>
        </div>
    );
}
